import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/utils/api";
import { useAuth } from "@/state/AuthContext";

export function PlatformTenantsPage() {
  const [tenants, setTenants] = useState<Record<string, unknown>[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    const data = await apiFetch<{ tenants: Record<string, unknown>[] }>(`/platform/tenants${qs}`);
    setTenants(data.tenants);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader title="Platform — Tenants" />
      <form
        className="flex gap-2 max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          load(search).catch(() => {});
        }}
      >
        <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button type="submit" variant="secondary">Search</Button>
      </form>
      <DataTable
        columns={[
          {
            key: "name",
            header: "Organization",
            render: (t) => (
              <Link to={`/admin/platform/tenants/${t.id}`} className="font-medium text-[#1e3a4f] hover:underline">
                {String(t.name)}
              </Link>
            ),
          },
          { key: "plan", header: "Plan", render: (t) => String(t.plan_label) },
          { key: "status", header: "Status", render: (t) => <StatusBadge status={String(t.subscription_status)} /> },
          { key: "seats", header: "Seats", className: "text-right", render: (t) => String(t.seat_count) },
        ]}
        rows={tenants}
        rowKey={(t) => String(t.id)}
      />
    </div>
  );
}

export function PlatformTenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { enterSupport, user } = useAuth();
  const [detail, setDetail] = useState<{
    tenant: Record<string, unknown>;
    members: Record<string, unknown>[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enteringSupport, setEnteringSupport] = useState(false);

  const load = async () => {
    if (!tenantId) return;
    const data = await apiFetch<typeof detail>(`/platform/tenants/${tenantId}`);
    setDetail(data);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const action = async (path: string) => {
    await apiFetch(`/platform/tenants/${tenantId}/${path}`, { method: "POST" });
    await load();
  };

  const viewAsSupport = async () => {
    if (!tenantId) return;
    setEnteringSupport(true);
    const result = await enterSupport(tenantId);
    setEnteringSupport(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error ?? "Failed to enter support mode");
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!detail) return null;

  const t = detail.tenant;
  return (
    <div className="space-y-4">
      <Link to="/admin/platform" className="text-sm text-[#1e3a4f] hover:underline">← All tenants</Link>
      <PageHeader title={String(t.name)} subtitle={String(t.slug)} />
      <div className="flex flex-wrap gap-2">
        <StatusBadge status={String(t.subscription_status)} />
        <span className="text-sm">Plan: {String(t.plan_label)}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          className="bg-violet-700 hover:bg-violet-800 text-white"
          disabled={enteringSupport}
          onClick={() => void viewAsSupport()}
        >
          {enteringSupport ? "Opening…" : "View as support"}
        </Button>
        {user?.supportMode && tenantId === String(t.id) && (
          <span className="text-sm text-violet-700 self-center font-medium">Active support session</span>
        )}
        <Button size="sm" variant="secondary" onClick={() => action("extend-trial")}>+7 Trial</Button>
        <Button size="sm" variant="secondary" onClick={() => action("extend-grace")}>+7 Grace</Button>
        <Button size="sm" variant="secondary" onClick={() => action("suspend")}>Suspend</Button>
        <Button size="sm" onClick={() => action("unsuspend")}>Unsuspend</Button>
      </div>
      <DataTable
        columns={[
          { key: "email", header: "Email", render: (m) => String(m.email) },
          { key: "name", header: "Name", render: (m) => String(m.display_name) },
          { key: "role", header: "Role", render: (m) => String(m.role_label) },
          { key: "status", header: "Status", render: (m) => String(m.status) },
        ]}
        rows={detail.members}
        rowKey={(m) => String(m.email)}
      />
    </div>
  );
}
